/**
 * Eenkleurige line-icons gebruikt in de Composer en in de hover-toolbar
 * van een bericht. Elk icon is een 1.5-stroke Feather-achtige SVG die
 * `currentColor` erft — handelig om kleur te sturen via de wrapper-class
 * (text-slate-300 / text-amber-300 / etc.).
 *
 * Bewust gegroepeerd op één plek: zo zien 'n nieuw aangeroepen icon-set
 * er consistent uit zonder dat we per component opnieuw aan strokeWidth
 * of viewBox-conventies hoeven te denken.
 */

type Props = { className?: string; size?: number };

function svg(d: JSX.Element, p: Props) {
  return (
    <svg
      width={p.size ?? 20}
      height={p.size ?? 20}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={p.className}
    >
      {d}
    </svg>
  );
}

export const IconSmile = (p: Props) =>
  svg(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M8 14s1.5 2 4 2 4-2 4-2" />
      <line x1="9" y1="9" x2="9.01" y2="9" />
      <line x1="15" y1="9" x2="15.01" y2="9" />
    </>,
    p,
  );

export const IconPaperclip = (p: Props) =>
  svg(
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />,
    p,
  );

export const IconMic = (p: Props) =>
  svg(
    <>
      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </>,
    p,
  );

export const IconStop = (p: Props) =>
  svg(<rect x="7" y="7" width="10" height="10" rx="1.5" fill="currentColor" />, p);

export const IconSend = (p: Props) =>
  svg(
    <>
      <path d="M22 2 11 13" />
      <path d="M22 2 15 22 11 13 2 9 22 2Z" />
    </>,
    { ...p, size: p.size ?? 18 },
  );

export const IconCamera = (p: Props) =>
  svg(
    <>
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </>,
    p,
  );

export const IconFile = (p: Props) =>
  svg(
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </>,
    p,
  );

export const IconPoll = (p: Props) =>
  svg(
    <>
      <line x1="6" y1="20" x2="6" y2="12" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="18" y1="20" x2="18" y2="14" />
    </>,
    p,
  );

export const IconLocation = (p: Props) =>
  svg(
    <>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </>,
    p,
  );

export const IconPin = (p: Props) =>
  svg(
    <>
      <line x1="12" y1="17" x2="12" y2="22" />
      <path d="M9 17h6l-1-5 3-2v-3H7v3l3 2-1 5z" />
    </>,
    p,
  );

export const IconReply = (p: Props) =>
  svg(
    <>
      <polyline points="9 17 4 12 9 7" />
      <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
    </>,
    p,
  );

export const IconForward = (p: Props) =>
  svg(
    <>
      <polyline points="15 17 20 12 15 7" />
      <path d="M4 18v-2a4 4 0 0 1 4-4h12" />
    </>,
    p,
  );

export const IconPencil = (p: Props) =>
  svg(
    <>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4z" />
    </>,
    p,
  );

export const IconTrash = (p: Props) =>
  svg(
    <>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </>,
    p,
  );

export const IconClose = (p: Props) =>
  svg(
    <>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </>,
    p,
  );
